import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Pagination,
  Stack,
  Avatar,
  Divider,
  TextareaAutosize,
} from '@mui/material';
import {
  Visibility,
  Flag,
  CheckCircle,
  Cancel,
  Block,
  Search,
  FilterList,
  Refresh,
  Home,
  Person,
  CalendarToday,
  LocationOn,
} from '@mui/icons-material';
import { ModerationListing, ModerationAction, ModerationFilters, ModerationRecord } from '@/types';
import { apiClient } from '@/utils/api-client';
import { useDebounce } from '@/hooks/useDebounce';

interface ListingModerationPanelProps {
  onError?: (error: string) => void;
}

export const ListingModerationPanel: React.FC<ListingModerationPanelProps> = ({ onError }) => {
  const [listings, setListings] = useState<ModerationListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<ModerationListing | null>(null);
  const [moderationDialogOpen, setModerationDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [moderationAction, setModerationAction] = useState<ModerationAction>({
    action: 'approve',
    reason: '',
    notes: '',
  });
  const [submittingModeration, setSubmittingModeration] = useState(false);
  
  // Filters and pagination
  const [filters, setFilters] = useState<ModerationFilters>({
    status: undefined,
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    limit: 20,
    offset: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Debounced search
  const debouncedSearch = useDebounce(filters.search || '', 500);

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (debouncedSearch) queryParams.append('search', debouncedSearch);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());

      const response = await apiClient.get<{
        listings: ModerationListing[];
        total: number;
        hasMore: boolean;
      }>(`/admin/listings?${queryParams.toString()}`);

      if (response.success && response.data) {
        setListings(response.data.listings);
        setTotalCount(response.data.total);
      } else {
        throw new Error('Failed to fetch listings');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load listings';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, onError]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleFilterChange = (key: keyof ModerationFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset to first page when filters change
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * (prev.limit || 20),
    }));
  };

  const handleModerationAction = async () => {
    if (!selectedListing || !moderationAction.reason.trim()) {
      return;
    }

    try {
      setSubmittingModeration(true);
      
      const response = await apiClient.post(
        `/admin/listings/${selectedListing.listingId}/moderate`,
        moderationAction
      );

      if (response.success) {
        // Refresh the listings
        await fetchListings();
        
        // Close dialogs and reset state
        setModerationDialogOpen(false);
        setSelectedListing(null);
        setModerationAction({
          action: 'approve',
          reason: '',
          notes: '',
        });
      } else {
        throw new Error('Failed to submit moderation action');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to submit moderation action';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setSubmittingModeration(false);
    }
  };

  const openModerationDialog = (listing: ModerationListing, action: ModerationAction['action']) => {
    setSelectedListing(listing);
    setModerationAction({
      action,
      reason: '',
      notes: '',
    });
    setModerationDialogOpen(true);
  };

  const openDetailDialog = (listing: ModerationListing) => {
    setSelectedListing(listing);
    setDetailDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'flagged': return 'info';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'approve': return <CheckCircle color="success" />;
      case 'reject': return <Cancel color="error" />;
      case 'flag': return <Flag color="warning" />;
      case 'suspend': return <Block color="error" />;
      default: return null;
    }
  };

  const totalPages = Math.ceil(totalCount / (filters.limit || 20));

  if (loading && listings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          Listing Moderation
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchListings}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search listings"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="flagged">Flagged</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy || 'createdAt'}
                  label="Sort By"
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  <MenuItem value="createdAt">Created Date</MenuItem>
                  <MenuItem value="updatedAt">Updated Date</MenuItem>
                  <MenuItem value="moderatedAt">Moderated Date</MenuItem>
                  <MenuItem value="flagCount">Flag Count</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={filters.sortOrder || 'desc'}
                  label="Order"
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value as 'asc' | 'desc')}
                >
                  <MenuItem value="desc">Newest First</MenuItem>
                  <MenuItem value="asc">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Listings Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Listing</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listings.map((listing) => (
                <TableRow key={listing.listingId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={listing.photos[0]}
                        sx={{ width: 48, height: 48, mr: 2 }}
                        variant="rounded"
                      >
                        <Home />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {listing.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {listing.address.city}, {listing.address.state}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{listing.hostName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={listing.moderationStatus}
                      color={getStatusColor(listing.moderationStatus) as any}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Flag sx={{ mr: 1, fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="body2">{listing.flagCount}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(listing.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openDetailDialog(listing)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {listing.moderationStatus === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              onClick={() => openModerationDialog(listing, 'approve')}
                              color="success"
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              onClick={() => openModerationDialog(listing, 'reject')}
                              color="error"
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Flag">
                        <IconButton
                          size="small"
                          onClick={() => openModerationDialog(listing, 'flag')}
                          color="warning"
                        >
                          <Flag />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Suspend">
                        <IconButton
                          size="small"
                          onClick={() => openModerationDialog(listing, 'suspend')}
                          color="error"
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {listings.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No listings found matching your criteria.
            </Typography>
          </Box>
        )}
      </Card>

      {/* Moderation Action Dialog */}
      <Dialog
        open={moderationDialogOpen}
        onClose={() => setModerationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getActionIcon(moderationAction.action)}
            <Typography variant="h6" sx={{ ml: 1, textTransform: 'capitalize' }}>
              {moderationAction.action} Listing
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedListing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedListing.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                by {selectedListing.hostName}
              </Typography>
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Reason (Required)"
            value={moderationAction.reason}
            onChange={(e) => setModerationAction(prev => ({ ...prev, reason: e.target.value }))}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            required
          />
          
          <TextField
            fullWidth
            label="Additional Notes (Optional)"
            value={moderationAction.notes}
            onChange={(e) => setModerationAction(prev => ({ ...prev, notes: e.target.value }))}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModerationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleModerationAction}
            variant="contained"
            disabled={!moderationAction.reason.trim() || submittingModeration}
            startIcon={submittingModeration ? <CircularProgress size={16} /> : getActionIcon(moderationAction.action)}
          >
            {submittingModeration ? 'Processing...' : `${moderationAction.action.charAt(0).toUpperCase() + moderationAction.action.slice(1)} Listing`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Listing Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Listing Details
        </DialogTitle>
        <DialogContent>
          {selectedListing && (
            <Box>
              {/* Basic Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedListing.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Person sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">Host: {selectedListing.hostName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    {selectedListing.address.street}, {selectedListing.address.city}, {selectedListing.address.state}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    Created: {new Date(selectedListing.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Status and Flags */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Moderation Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip
                    label={selectedListing.moderationStatus}
                    color={getStatusColor(selectedListing.moderationStatus) as any}
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Typography variant="body2">
                    Flags: {selectedListing.flagCount} | Reports: {selectedListing.reportCount}
                  </Typography>
                </Box>
                {selectedListing.lastModeratedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Last moderated: {new Date(selectedListing.lastModeratedAt).toLocaleString()} by {selectedListing.lastModeratedBy}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Description
                </Typography>
                <Typography variant="body2">
                  {selectedListing.description}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Property Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Property Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Max Guests:</strong> {selectedListing.maxGuests}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Bedrooms:</strong> {selectedListing.bedrooms}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Bathrooms:</strong> {selectedListing.bathrooms}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Base Price:</strong> ${selectedListing.pricing.basePrice}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Moderation History */}
              {selectedListing.moderationHistory && selectedListing.moderationHistory.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Moderation History
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {selectedListing.moderationHistory.map((record, index) => (
                      <Box key={record.moderationId} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {getActionIcon(record.action)}
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', textTransform: 'capitalize' }}>
                            {record.action}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 'auto' }}>
                            {new Date(record.moderatedAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>By:</strong> {record.moderatedByName}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Reason:</strong> {record.reason}
                        </Typography>
                        {record.notes && (
                          <Typography variant="body2">
                            <strong>Notes:</strong> {record.notes}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};